import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api, { setSuppressAuthRedirect, setAuthToken, clearAuthToken, hydrateAuthFromIDB } from "@/lib/api";

// Pages - New Structure
import LandingPage from "@/pages/LandingPage";
import LandingPageEN from "@/pages/LandingPageEN";
import LoginPage from "@/pages/LoginPage";
import RichiestaAccessoPage from "@/pages/RichiestaAccessoPage";
import OggiPage from "@/pages/OggiPage";
import OrganizzaPage from "@/pages/OrganizzaPage";
import BenesserePage from "@/pages/BenesserePage";
import PercorsiPage from "@/pages/PercorsiPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import ClinicalDocumentationPage from "@/pages/ClinicalDocumentationPage";
import PrivacyPage from "@/pages/PrivacyPage";
import PrivacyBetaPage from "@/pages/PrivacyBetaPage";
import TermsPage from "@/pages/TermsPage";

// Legacy pages (still used inside hub pages)
import Dashboard from "@/pages/Dashboard";
import ExpensesPage from "@/pages/ExpensesPage";
import HabitsPage from "@/pages/HabitsPage";
import MoodPage from "@/pages/MoodPage";
import VoiceMoodPage from "@/pages/VoiceMoodPage";
import CalendarPage from "@/pages/CalendarPage";
import NeedsPage from "@/pages/NeedsPage";
import DiaryPage from "@/pages/DiaryPage";
import NotesPage from "@/pages/NotesPage";
import LifeTasksPage from "@/pages/LifeTasksPage";
import NutritionPage from "@/pages/NutritionPage";
import EmaPage from "@/pages/EmaPage";
import AssertivitaPage from "@/pages/AssertivitaPage";

// Components
import Navigation from "@/components/Navigation";
import { OnboardingDialog, useOnboarding } from "@/components/Disclaimer";
import OnboardingFlow from "@/components/OnboardingFlow";
import BetaWelcomeModal from "@/components/onboarding/BetaWelcomeModal";
import AccessCodeGate from "@/components/onboarding/AccessCodeGate";
import { PeriodProvider } from "@/hooks/usePeriod";
import HabitTipsButton from "@/components/HabitTipsButton";
import { registerServiceWorker } from "@/lib/pushManager";

// Local storage key for onboarding completion
const ONBOARDING_COMPLETE_KEY = "yourlifemap_onboarding_complete";
const HOME_PAGE_PREF_KEY = "yourlifemap_home_page";

// Debug logging helper - only logs in development
const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

const debugError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};

// Get user's preferred home page
const getPreferredHomePage = () => {
  return localStorage.getItem(HOME_PAGE_PREF_KEY) || "/oggi";
};

// ============================================
// AUTH CALLBACK - Processes session_id from URL
// ============================================
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Autenticazione in corso...");

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from hash fragment
      const fullUrl = window.location.href;
      const hash = window.location.hash;
      
      debugLog("[OAuth Debug] Full URL:", fullUrl);
      debugLog("[OAuth Debug] Hash from window:", hash);
      debugLog("[OAuth Debug] Hash from location:", location.hash);
      
      // Try multiple ways to extract session_id
      let sessionId = null;
      
      // Method 1: From hash
      const hashMatch = hash.match(/session_id=([^&]+)/);
      if (hashMatch) {
        sessionId = hashMatch[1];
        debugLog("[OAuth Debug] Found session_id in hash");
      }
      
      // Method 2: From full URL (in case routing stripped the hash)
      if (!sessionId && fullUrl.includes('session_id=')) {
        const urlMatch = fullUrl.match(/session_id=([^&]+)/);
        if (urlMatch) {
          sessionId = urlMatch[1];
          debugLog("[OAuth Debug] Found session_id in full URL");
        }
      }

      debugLog("[OAuth Debug] Session ID:", sessionId ? `Found (${sessionId.substring(0, 20)}...)` : "NOT FOUND");

      if (!sessionId) {
        debugError("[OAuth Debug] NO SESSION ID - Check the URL format");
        setStatus("Sessione non trovata. Reindirizzamento...");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
        return;
      }

      try {
        debugLog("[OAuth Debug] Calling /api/auth/session...");
        const response = await api.post('/auth/session', { session_id: sessionId });
        
        debugLog("[OAuth Debug] Session created successfully!", response.data);
        
        // CRITICAL FIX: Save session_token to localStorage for Bearer-based auth.
        // This bypasses Chrome mobile / Kubernetes ingress SameSite cookie issues
        // where httpOnly cookies sometimes fail to be sent after OAuth redirect.
        if (response.data.session_token) {
          setAuthToken(response.data.session_token);
          sessionStorage.setItem('_auth_bridge', response.data.session_token);
        }
        
        setStatus("Login completato!");
        
        const userEmail = response.data.email;
        const isAdmin = ["piccinini.ps@gmail.com"].includes(userEmail);
        const accessCode = localStorage.getItem('access_code');
        
        if (accessCode) {
          debugLog("[OAuth Debug] Access code found, activating subscription...");
          try {
            await api.post('/stripe/create-checkout', {
              origin_url: window.location.origin,
              promo_code: accessCode
            });
            localStorage.removeItem('access_code');
          } catch (promoError) {
            debugLog("[OAuth Debug] Promo activation note:", promoError.response?.data?.detail || promoError.message);
          }
        }
        
        // CRITICAL: Use window.location.href instead of navigate()
        // This forces a full page reload, ensuring cookies are fully processed
        // React Router navigate() doesn't reload, causing cookie race conditions on Chrome
        let targetPage = getPreferredHomePage();
        if (!isAdmin && !accessCode) {
          try {
            const subResponse = await api.get('/stripe/subscription-status');
            if (!subResponse.data.is_active) {
              targetPage = "/richiedi-accesso";
            }
          } catch {
            targetPage = "/richiedi-accesso";
          }
        }
        
        window.location.href = targetPage;

      } catch (error) {
        debugError("[OAuth Debug] ERROR:", error);
        debugError("[OAuth Debug] Error response:", error.response?.data);
        debugError("[OAuth Debug] Error status:", error.response?.status);
        setStatus(`Errore: ${error.response?.data?.detail || error.message}`);
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      }
    };

    processAuth();
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
};

// ============================================
// PROTECTED ROUTE - Verifies auth AND subscription
// ============================================
const ADMIN_EMAILS = ["piccinini.ps@gmail.com"];

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [hasAccess, setHasAccess] = useState(
    location.state?.user ? null : null // Will check subscription
  );
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    const checkAuthAndAccess = async () => {
      let currentUser = location.state?.user;
      
      // Step 1: Verify authentication
      if (!currentUser) {
        setSuppressAuthRedirect(true);
        // Retry helper: on network/5xx errors we keep retrying (backend may be
        // sleeping/cold-starting). We only redirect to /login on EXPLICIT 401/403,
        // never on transient network errors — those would log the user out the
        // first time they open the app after a long sleep.
        const isAuthError = (err) => {
          const s = err?.response?.status;
          return s === 401 || s === 403;
        };
        const attempts = [0, 1500, 4000, 8000]; // retry after these delays (ms)
        let response = null;
        let lastError = null;
        for (let i = 0; i < attempts.length; i++) {
          if (attempts[i] > 0) await new Promise(r => setTimeout(r, attempts[i]));
          try {
            response = await api.get('/auth/me');
            break;
          } catch (err) {
            lastError = err;
            // Stop early ONLY on explicit auth failure
            if (isAuthError(err)) break;
            // else: keep retrying (network / 502 / 503 / timeout)
          }
        }
        if (response) {
          currentUser = response.data;
          setUser(currentUser);
          setIsAuthenticated(true);
          setSuppressAuthRedirect(false);
        } else {
          setSuppressAuthRedirect(false);
          if (isAuthError(lastError)) {
            // Real auth failure → redirect to login
            setIsAuthenticated(false);
            navigate("/login", { replace: true });
          } else {
            // Network failure after all retries → show a soft error in the UI
            // by keeping isAuthenticated=null so the spinner stays, instead of
            // logging the user out. The user can manually pull-to-refresh.
            setIsAuthenticated(false);
            navigate("/login", { replace: true, state: { networkError: true } });
          }
          return;
        }
      }
      
      // Step 2: Check if admin (bypass subscription check)
      if (ADMIN_EMAILS.includes(currentUser.email)) {
        debugLog("[ProtectedRoute] Admin user, granting access");
        // Register native push (APNs/FCM) now that we are authenticated.
        try {
          const { setupNativePush } = await import('@/lib/nativePush');
          const { isNativePlatform } = await import('@/lib/platform');
          if (isNativePlatform()) setupNativePush().catch(() => {});
        } catch { /* ignore */ }
        setHasAccess(true);
        return;
      }
      
      // Step 3: Check subscription status
      try {
        const subResponse = await api.get('/stripe/subscription-status');
        if (subResponse.data.is_active) {
          debugLog("[ProtectedRoute] User has active subscription");
          // Register native push (APNs/FCM) now that we are authenticated.
          try {
            const { setupNativePush } = await import('@/lib/nativePush');
            const { isNativePlatform } = await import('@/lib/platform');
            if (isNativePlatform()) setupNativePush().catch(() => {});
          } catch { /* ignore */ }
          setHasAccess(true);
        } else {
          debugLog("[ProtectedRoute] No active subscription, redirecting");
          setHasAccess(false);
          navigate("/richiedi-accesso", { replace: true, state: { user: currentUser } });
        }
      } catch (subError) {
        debugLog("[ProtectedRoute] Could not verify subscription:", subError.message);
        // If can't check subscription, require access code
        setHasAccess(false);
        navigate("/richiedi-accesso", { replace: true, state: { user: currentUser } });
      }
    };

    checkAuthAndAccess();
  }, [location.state?.user, navigate]);

  // Loading state
  if (isAuthenticated === null || (isAuthenticated && hasAccess === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  // Not authenticated or no access
  if (isAuthenticated === false || hasAccess === false) {
    return null;
  }

  // Authenticated AND has access - render children with user
  return children({ user, setUser });
};

// ============================================
// APP LAYOUT
// ============================================
const AppLayout = ({ children, user, onLogout }) => {
  const { showOnboarding: showDisclaimer, completeOnboarding: completeDisclaimer, checked } = useOnboarding();
  const [showNewOnboarding, setShowNewOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  // 2026-05-21: beta welcome modal (separate from legacy OnboardingFlow)
  const [showBetaWelcome, setShowBetaWelcome] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Beta welcome modal: shown only to whitelisted Revela beta testers who haven't
    // accepted yet. Non-whitelisted users (e.g. old test accounts) must NOT see
    // the "Grazie per partecipare al beta" message — they're not in the beta.
    let cancelled = false;
    const checkAndShow = async () => {
      if (!user || !user.user_id || user.onboarding_completed !== false) {
        if (!cancelled) setShowBetaWelcome(false);
        return;
      }
      try {
        const res = await api.get("/voice-mood/access");
        if (!cancelled) setShowBetaWelcome(!!res.data?.has_access);
      } catch {
        if (!cancelled) setShowBetaWelcome(false);
      }
    };
    checkAndShow();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.onboarding_completed]);

  useEffect(() => {
    // Check URL param for forcing onboarding (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const forceOnboarding = urlParams.get('onboarding') === 'true';
    
    if (forceOnboarding) {
      // Reset onboarding for testing
      localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
      setShowNewOnboarding(true);
      setOnboardingChecked(true);
      return;
    }
    
    // Check if new user needs onboarding - but skip for returning users
    const hasCompletedOnboarding = localStorage.getItem(ONBOARDING_COMPLETE_KEY);
    
    // If user is already logged in (has user data), they're returning - mark onboarding complete
    if (user && user.user_id) {
      // Returning user - auto-complete onboarding
      localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
      setShowNewOnboarding(false);
    } else if (!hasCompletedOnboarding) {
      setShowNewOnboarding(true);
    }
    setOnboardingChecked(true);
  }, [location.search, user]);

  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    setShowNewOnboarding(false);
    // Remove the query param from URL
    window.history.replaceState({}, '', window.location.pathname);
  };

  // Show new onboarding flow ONLY for truly new users (not returning users)
  if (onboardingChecked && showNewOnboarding && !user?.user_id) {
    return <OnboardingFlow onComplete={handleOnboardingComplete} />;
  }

  return (
    <PeriodProvider>
      <div className="min-h-screen bg-background">
        <Navigation user={user} onLogout={onLogout} />
        <main className="container mx-auto px-4 py-8 max-w-7xl pb-24">
          {children}
        </main>
        {/* Legacy disclaimer dialog — suppressed when:
            - beta consent has been given (Laura's beta flow already covers it)
            - the AccessCodeGate is up (would stack modals and cause loops)
            - the user hasn't completed onboarding yet (BetaWelcomeModal will mount once /voice-mood/access resolves;
              showing the legacy disclaimer in the meantime caused a brief "double disclaimer" flash for new users). */}
        {checked && !showBetaWelcome && user?.onboarding_completed && !user?.beta_consent_accepted && !user?.requires_new_access_code && (
          <OnboardingDialog open={showDisclaimer} onComplete={completeDisclaimer} />
        )}
        <BetaWelcomeModal
          // Don't mount BetaWelcome while the AccessCodeGate is up: the user must
          // first clear the legacy access-code flag, otherwise accept-beta-consent
          // would 403 and we'd loop.
          open={showBetaWelcome && !user?.requires_new_access_code}
          userName={user?.name}
          onComplete={() => {
            setShowBetaWelcome(false);
            // Force a fresh /auth/me fetch so that user.beta_consent_accepted=true
            // propagates and prevents the legacy disclaimer from mounting.
            window.location.reload();
          }}
        />
        {/* 2026-06: legacy users must enter the new access code (LPAMINDWAVE)
            before they can use the app. Data preserved; only access gated. */}
        <AccessCodeGate
          open={!!user?.requires_new_access_code}
          userName={user?.name}
          onVerified={() => window.location.reload()}
        />
      </div>
    </PeriodProvider>
  );
};

// ============================================
// APP ROUTER - CRITICAL: Check session_id synchronously
// ============================================
const AppRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Logout handler
  const handleLogout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error("Logout error:", error);
    }
    clearAuthToken();
    window.location.href = "/";
  }, []);

  // Handle login from landing page
  const handleLandingLogin = () => {
    navigate('/login');
  };

  // CRITICAL: Check for session_id SYNCHRONOUSLY during render
  // This runs BEFORE useEffect - prevents race conditions
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    debugLog("[AppRouter] Detected session_id, showing AuthCallback");
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing page - public */}
      <Route path="/" element={<LandingPage onLogin={handleLandingLogin} />} />

      {/* Landing page - English version (used for The Liquid Factory etc.) */}
      <Route path="/en" element={<LandingPageEN />} />
      
      {/* Login page - public */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Auth callback - public */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Access code request page - for users without subscription */}
      <Route path="/richiedi-accesso" element={<RichiestaAccessoPage onLogout={handleLogout} />} />
      
      {/* Legal pages - public */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/privacy-beta" element={<PrivacyBetaPage />} />
      <Route path="/termini" element={<TermsPage />} />
      
      {/* ============ NEW MAIN ROUTES ============ */}
      
      {/* OGGI - Daily Dashboard */}
      <Route path="/oggi" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <OggiPage user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      
      {/* ORGANIZZA - Practical Life Hub */}
      <Route path="/organizza/*" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <OrganizzaPage />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      
      {/* BENESSERE - Wellness Hub */}
      <Route path="/benessere/*" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <BenesserePage />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      
      {/* PERCORSI - Premium Journeys */}
      <Route path="/percorsi/*" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <PercorsiPage />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      
      {/* ============ SETTINGS & ADMIN ============ */}
      
      <Route path="/impostazioni" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <SettingsPage />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <AdminPage />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/clinico/documentazione" element={
        <ProtectedRoute>
          {({ user }) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <ClinicalDocumentationPage />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      
      {/* ============ LEGACY REDIRECTS ============ */}
      {/* Redirect old routes to new structure */}
      <Route path="/dashboard" element={<Navigate to="/oggi" replace />} />
      <Route path="/spese" element={<Navigate to="/organizza" replace />} />
      <Route path="/compiti" element={<Navigate to="/organizza" replace />} />
      <Route path="/calendario" element={<Navigate to="/organizza" replace />} />
      <Route path="/note" element={<Navigate to="/organizza" replace />} />
      <Route path="/abitudini" element={<Navigate to="/benessere" replace />} />
      <Route path="/umore" element={<Navigate to="/benessere" replace />} />
      <Route path="/diario" element={<Navigate to="/benessere" replace />} />
      <Route path="/bisogni" element={<Navigate to="/benessere" replace />} />
      <Route path="/alimentazione" element={<Navigate to="/benessere" replace />} />
      <Route path="/assertivita" element={<Navigate to="/percorsi" replace />} />
      
      {/* Catch all - redirect to oggi */}
      <Route path="*" element={<Navigate to="/oggi" replace />} />
    </Routes>
  );
};

// ============================================
// MAIN APP
// ============================================
function App() {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Hydrate auth token from the most reliable backend before first API call.
    // On native: Capacitor Preferences (Keychain/EncryptedPrefs)
    // On web:    localStorage > IndexedDB
    hydrateAuthFromIDB().finally(() => {
      setHydrated(true);
      // Note: native APNs/FCM registration is intentionally NOT triggered here.
      // It's done inside ProtectedRoute once the user is authenticated, so the
      // `/api/push/native-register` call has a valid session token.
    });
    registerServiceWorker();
  }, []);

  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
